// miniapp/src/components/chat/EditContextBar.tsx
//
// Edit-message phase — Telegram-style "Editing message" preview rendered
// above the composer input when an edit target is active. Visually
// parallels ReplyContextBar (same blue accent stripe, same × cancel
// affordance) so the technician's eye treats both bars as "you're in a
// special compose mode".
//
// State lives on TicketDetail (plain useState, per PATTERNS.md).
// Purely visual + onCancel — no API calls here.

import { X } from 'lucide-react';
import type { Message } from '../../types/ticket';

export interface EditContextBarProps {
  /** The message currently being edited. */
  target: Message;
  /** Tap × to abandon the edit and return to normal compose mode. */
  onCancel: () => void;
}

/** First ~80 chars of the target's current text, single-lined. Edits
 *  always operate on text or captions (gated upstream), so we don't need
 *  the media-fallback branch the reply bar has. */
function previewText(msg: Message): string {
  if (msg.text && msg.text.trim()) {
    const collapsed = msg.text.replace(/\s+/g, ' ').trim();
    return collapsed.length > 80 ? collapsed.slice(0, 80) + '…' : collapsed;
  }
  return '(empty)';
}

export function EditContextBar({ target, onCancel }: EditContextBarProps) {
  const preview = previewText(target);
  return (
    <div
      className="flex-none bg-white border-t px-4 py-2"
      role="region"
      aria-label="Editing message"
    >
      <div className="flex items-center gap-3 max-w-4xl mx-auto">
        <div
          className="flex-shrink-0 w-[3px] self-stretch rounded-full"
          style={{ background: '#007AFF' }}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold leading-tight" style={{ color: '#007AFF' }}>
            Editing message
          </div>
          <div className="text-[13px] text-gray-700 leading-tight truncate">
            {preview}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel edit"
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 active:text-gray-900"
        >
          <X className="w-5 h-5" strokeWidth={2} aria-hidden />
        </button>
      </div>
    </div>
  );
}
